// import { router, usePathname } from 'expo-router';
import { useCallback, useMemo } from 'react';
// import { Alert, Linking } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { UniversalButtonData, UserInfo } from '@cobu/types';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
// import { useGetReferralOffer } from '@/services';
import { showErrorMessage } from '../../utils/messageToast';
import { useSelector } from 'react-redux';
import { Strings } from '../../constants/Strings';
import { RootState } from '../../store';
import { logger } from '../../utils/logger';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useResidentRestrictions } from '../../hooks/useResidentRestrictions';

const strings = Strings.get();

export const useUniversalButton = (universalButtonData: UniversalButtonData | null) => {
  const user = useSelector((state: RootState) => state.auth.user) as UserInfo | null;
  const navigate = useNavigate();
  // const pathname = usePathname();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const { residentsCanCreateEvents, showGroups } = useFeatureFlags();
  const { canCreateEvent } = useResidentRestrictions();

  const currentPageName = useMemo(() => {
    // Extract the current tab from pathname like "/(tabs)/events" -> "events"
    const match = pathname.match(/\/\(tabs\)\/(.+)/);
    return match ? match[1] : pathname.split('/').pop() || '';
  }, [pathname]);

  const onPress = useCallback(() => {
    if (
      !universalButtonData ||
      !universalButtonData.linkType ||
      (universalButtonData.linkType === 'External' && !universalButtonData.linkURL)
    ) {
      showErrorMessage(`${strings.common.error.title}: ${strings.universalButton.errors.linkOpenFailed}`);
      return;
    }

    const { linkType, linkURL, uuid: tooltipUuid } = universalButtonData;

    switch (linkType) {
      case 'Referral':
        navigate('/perks/referral');
        break;
      case 'Events':
        navigate('/events');
        break;
      case 'Groups':
        if (showGroups) {
          navigate('/groups');
        }
        break;
      case 'Perks':
        navigate('/perks');
        break;
      case 'Marketplace':
        navigate('/marketplace');
        break;
      case 'CreateGroupEvent':
        if (residentsCanCreateEvents && canCreateEvent) {
          navigate('/events/create-group-event');
        }
        break;
      case 'External':
        if (!linkURL) break;
        
        if (linkURL.includes('typeform')) {
          navigate('/webview', {
            state: {
              url: linkURL,
              title: universalButtonData.buttonTitle || strings.webView.surveyFallbackTitle,
            }
          });
        } else if (linkURL.startsWith("sms:") || linkURL.startsWith("mailto:")) {
          navigate(linkURL);
        } else {
          const normalizedUrl = `https://${linkURL}`;
          window.open(normalizedUrl, '_blank');
        }
        break;
      default:
        break;
    }

    trackButtonClick(user, universalButtonData);
  }, [universalButtonData, user, navigate, residentsCanCreateEvents, canCreateEvent, showGroups]);

  const isDisabled = useMemo(() => {
    if (universalButtonData?.linkType === 'Marketplace' && currentPageName === 'marketplace') {
      return true;
    }
    if (universalButtonData?.linkType === 'Events' && currentPageName === 'events') {
      return true;
    }
    if (universalButtonData?.linkType === 'Groups' && (currentPageName === 'groups' || !showGroups)) {
      return true;
    }
    if (universalButtonData?.linkType === 'Perks' && currentPageName === 'perks') {
      return true;
    }
    if (universalButtonData?.linkType === 'CreateGroupEvent' && (!residentsCanCreateEvents || !canCreateEvent)) {
      return true;
    }
    return false;
  }, [universalButtonData?.linkType, currentPageName, residentsCanCreateEvents, canCreateEvent, showGroups]);

  return {
    onPress,
    isDisabled,
    currentPageName,
  };
};

function trackButtonClick(user: UserInfo | null, universalButtonData: UniversalButtonData) {
  const properties = {
    buildingId: user?.building?.uuid,
    buildingName: user?.building?.name,
    userId: user?.uuid,
    buttonTitle: universalButtonData.buttonTitle,
    linkType: universalButtonData.linkType,
    linkURL: universalButtonData.linkURL,
    userName: `${user?.firstName} ${user?.lastName}`,
  };

  /* ANALYTICS_DISABLED: UNIVERSAL_BUTTON_CLICK ('universal_btn_click')
    analyticsService.trackEvent(AnalyticsEvent.UNIVERSAL_BUTTON_CLICK, properties);
    */
  logger.log('UNIVERSAL_BUTTON_CLICK', properties);
}
